"use strict";
// Copyright (C) Microsoft Corporation. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.returnToFocusDrivenInputMode = exports.getLastFocusDrivenInputMode = exports.setInputMode = exports.updateBodyInputModes = exports.isInputModeFocusDriven = exports.getInputMode = exports.setDoesDefaultToFocusDrivenInputMode = exports.doesDefaultToFocusDrivenInputMode = void 0;
const gamepadEvents_1 = require("../gamepadEvents");
const types_1 = require("../../types");
const focusFinder_1 = require("../search/focusFinder");
const focusManager_1 = require("./focusManager");
const initialFocusFinder_1 = require("../search/initialFocusFinder");
const PollingModeManager_1 = require("./PollingModeManager");
// Whether we've specified that we should default to focus-driven input modes.
// This is meant for platforms like TVs where focus-driven input modes are the
// only way of interfacing with the UX.
let defaultToFocusDrivenInputMode = false;
/**
 * @returns whether we should default to focus-driven input modes
 */
const doesDefaultToFocusDrivenInputMode = () => {
    return defaultToFocusDrivenInputMode;
};
exports.doesDefaultToFocusDrivenInputMode = doesDefaultToFocusDrivenInputMode;
/**
 * Sets whether we should default to focus-driven input modes
 *
 * @param value - whether we should default to focus-driven input modes
 */
const setDoesDefaultToFocusDrivenInputMode = (value) => {
    defaultToFocusDrivenInputMode = value;
};
exports.setDoesDefaultToFocusDrivenInputMode = setDoesDefaultToFocusDrivenInputMode;
// The last remembered focus-driven input mode
let lastFocusDrivenInputMode = types_1.DetailedInputMode.Gamepad;
// The last remembered input mode
let inputMode = types_1.DetailedInputMode.Mouse;
// The last remembered active input mode that required active user input
// DetailedInputMode.PassiveMouse does not count since the user did not perform
// a direct action to trigger an interaction on the screen
let lastActiveInputMode = types_1.DetailedInputMode.Mouse;
/**
 * Getter for the current input mode
 *
 *  @returns the current input mode
 */
const getInputMode = () => {
    return inputMode;
};
exports.getInputMode = getInputMode;
/**
 * @returns whether the current input mode is focus-driven (keyboard / gamepad)
 */
const isInputModeFocusDriven = () => {
    return (0, types_1.isFocusDriven)(lastActiveInputMode);
};
exports.isInputModeFocusDriven = isInputModeFocusDriven;
const updateBodyInputModes = () => {
    document.body.setAttribute('data-input-mode', inputMode);
    document.body.setAttribute('data-active-input-mode', lastActiveInputMode);
};
exports.updateBodyInputModes = updateBodyInputModes;
/**
 * Sets the next input mode, and focuses on the last focused interactable, if specified
 *
 *  @param inputMode - the input mode to set
 *  @param changeFocus - whether we should change focus to the last known focused interactable
 */
const setInputMode = (newInputMode, changeFocus, fromTabbing) => {
    if (inputMode === newInputMode ||
        (inputMode === types_1.DetailedInputMode.Mouse && newInputMode === types_1.DetailedInputMode.PassiveMouse)) {
        return;
    }
    // Only perform action when:
    // (1) switching from a non focus-driven input mode to a focus-driven one
    // (2) switching from a focus-driven input mode to a non focus-driven one, so long as it is not a passive mouse event
    // (3) switching from a passive mouse input mode to a non-passive mouse input mode
    const actionRequired = ((0, types_1.isFocusDriven)(lastActiveInputMode) !== (0, types_1.isFocusDriven)(newInputMode) &&
        newInputMode !== types_1.DetailedInputMode.PassiveMouse) ||
        (inputMode === types_1.DetailedInputMode.PassiveMouse && newInputMode === types_1.DetailedInputMode.Mouse);
    if (inputMode !== types_1.DetailedInputMode.PassiveMouse || newInputMode !== types_1.DetailedInputMode.Mouse) {
        gamepadEvents_1.GamepadEventEmitter.emit(gamepadEvents_1.InputModeEvent.Change, newInputMode);
    }
    if (newInputMode !== types_1.DetailedInputMode.PassiveMouse) {
        lastActiveInputMode = newInputMode;
    }
    inputMode = newInputMode;
    (0, exports.updateBodyInputModes)();
    if (actionRequired) {
        if (!(0, types_1.isFocusDriven)(inputMode)) {
            if (changeFocus && PollingModeManager_1.PollingModeManager.isNavigationPollingEnabled()) {
                (0, focusManager_1.blurCurrentFocus)();
            }
        }
        else {
            lastFocusDrivenInputMode = inputMode;
            if (changeFocus && PollingModeManager_1.PollingModeManager.isNavigationPollingEnabled()) {
                const currentlyFocusedInteractable = (0, focusManager_1.getCurrentlyFocusedInteractable)();
                if (currentlyFocusedInteractable) {
                    if ((0, focusFinder_1.isViewOnParentContainer)(currentlyFocusedInteractable)) {
                        currentlyFocusedInteractable.focus();
                        return;
                    }
                    else {
                        (0, initialFocusFinder_1.findInitialFocus)(fromTabbing);
                    }
                }
                else {
                    (0, initialFocusFinder_1.findInitialFocus)(fromTabbing);
                }
            }
        }
    }
};
exports.setInputMode = setInputMode;
/**
 * Getter for the last focus-driven input mode
 *
 *  @returns the last focus driven input mode. If none has been set, defaults to Gamepad.
 */
const getLastFocusDrivenInputMode = () => {
    return lastFocusDrivenInputMode;
};
exports.getLastFocusDrivenInputMode = getLastFocusDrivenInputMode;
/**
 * Sets the input mode to the last seen focus-driven input mode
 */
const returnToFocusDrivenInputMode = () => {
    (0, exports.setInputMode)(lastFocusDrivenInputMode, true);
};
exports.returnToFocusDrivenInputMode = returnToFocusDrivenInputMode;
//# sourceMappingURL=data:application/json;base64,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